import type { Entity, EntityEdge, EntityMap } from '@ms/yammer-data-support';
import type {
  AnswerStateType,
  ThreadDiscoveryHiddenState,
  ThreadFields_communicationsProjectSlot_project,
  ThreadReplySortOrder,
} from '@ms/yammer-graphql';

import type {
  File,
  Group,
  HomeFeedCardEdgeType,
  ImageFile,
  LiveEvent,
  Message,
  Network,
  RecommendationReason,
  RecommendedToplevelReplyEdge,
  ReplyEdge,
  ThreadShare,
  Topic,
  User,
} from '../types';

import type { ThreadScope } from './threadScope.types';

export * from './threadScope.types';

export interface Thread extends Entity {
  readonly databaseId: string;
  readonly telemetryId: string;
  readonly networkId: Network['id'];
  readonly groupId?: Group['id'];
  readonly broadcastId?: LiveEvent['broadcastId'];
  readonly hasAttachments: boolean;
  readonly communicationsProjectId?: ThreadFields_communicationsProjectSlot_project['id'];
  readonly communicationsProjectSlotId?: string;
  readonly communicationsProjectSlotScheduledAt?: number;
  readonly communicationsProjectSlotViewerCanCreateThread?: boolean;
  readonly repliesCount: number;
  readonly repliesCountOfUnseen: number;
  readonly sharesCount?: number;
  readonly threadStarterId: Message['id'];
  readonly moderatedEventAgentReplyIds?: Message['id'][];
  readonly headerImageId?: ImageFile['id'];
  readonly hiddenForNetworkInDiscovery: ThreadDiscoveryHiddenState;
  readonly bestReplyId?: Message['id'];
  readonly bestReplyMarkedByUserId?: User['id'];
  readonly recommendedTopLevelReplies?: RecommendedToplevelReplyEdge[];
  readonly replyEdges: ReplyEdge[];
  readonly hasPreviousPageOfReplies: boolean;
  readonly participantIds: User['id'][];
  readonly participantsCount: number;
  readonly topicIds: Topic['id'][];
  readonly isFollowing?: boolean;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly isUnreadInInbox: boolean;
  readonly viewerHasSeenThreadStarter: boolean;
  readonly viewerLastSeenMessageId?: Message['id']; // need this?
  readonly seenByCount?: number;
  readonly isDeleted: boolean;
  readonly viewerCanBookmark: boolean;
  readonly viewerCanClose: boolean;
  readonly viewerCanDelete: boolean;
  readonly viewerCanHideForNetworkInDiscovery: boolean;
  readonly viewerCanMarkBestReply: boolean;
  readonly viewerCanMarkVerifiedReply?: boolean;
  readonly viewerCanPin: boolean;
  readonly viewerCanPinInMyLeaders: boolean;
  readonly viewerCanPromote: boolean;
  readonly viewerCanMarkAsQuestion: boolean;
  readonly viewerCanMove: boolean;
  readonly viewerCanReply: boolean;
  readonly viewerCanPrivateReply: boolean;
  readonly viewerCanAccessThreadActivity: boolean;
  readonly viewerCanAddOrRemoveTopic: boolean;
  readonly isAnnouncement: boolean;
  readonly isClosed: boolean;
  readonly isDirectMessage: boolean;
  readonly isPromotedForViewer: boolean;
  readonly promotionEndTime?: number;
  readonly markSeenKey: string;
  readonly shareIds?: ThreadShare['id'][];
  readonly repliesSortedBy: ThreadReplySortOrder;
  readonly isForcedNotification: boolean;
  readonly wallOwnerId?: User['id'];
  readonly scope: ThreadScope;
  readonly viewerHasBookmarked: boolean;
  readonly viewerHasUpvoted: boolean;
  readonly upvoteCount: number;
  readonly replySmallFileUploadUrl: string;
  readonly isVisibleInNetworkQuestionFeeds: boolean;
  readonly messageContentSourceFileId?: File['id'];
  readonly isQueued?: boolean;
  readonly answerState?: AnswerStateType;
  readonly verifiedReplyId?: Message['id'];
  readonly verifiedReplyMarkedByUserId?: User['id'];
  readonly verifiedReplyMarkedAt?: number;
}

export type Threads = EntityMap<Thread>;

export type ThreadEdge = EntityEdge<Thread> & {
  readonly isThreadStarterPostedByCurrentUser?: boolean;
  readonly wasLastReplyPostedByCurrentUser?: boolean;
  readonly recommendationReason?: RecommendationReason['id'];
  readonly type?: HomeFeedCardEdgeType;
};

export type BestReplyFields = 'id' | 'bestReplyId' | 'bestReplyMarkedByUserId';

export type ThreadBestReplyProps = Pick<Thread, BestReplyFields>;

export type VerifiedReplyFields = 'id' | 'verifiedReplyId' | 'verifiedReplyMarkedByUserId' | 'verifiedReplyMarkedAt';

export type ThreadVerifiedReplyProps = Pick<Thread, VerifiedReplyFields>;

export type ThreadLoadError = 'NullThread';

export enum ThreadModerationStateChange {
  PENDING = 'PENDING',
  DISMISSED = 'DISMISSED',
  PUBLISHED = 'PUBLISHED',
}
